<template>

  <!-- Основной контейнер страницы -->
  <main class="item-page">
    <div class="item-shell">

      <!-- Левая часть — галерея -->
      <section class="left-pane">
        <h1 class="item-title">{{ title }}</h1>

        <div class="image-wrap" :class="{ 'image-wrap--empty': !hasImage }">
          <div v-if="hasImage" class="image-frame" @click="openModal(currentIndex)" role="button" tabindex="0" @keydown.enter="openModal(currentIndex)">
            <img
              :src="currentImageSrc"
              :alt="title"
              class="image"
              @error="onImageError(currentIndex)"
              ref="mainImg"
            />
            <div v-if="images.length > 1" class="img-counter">{{ currentIndex + 1 }} / {{ images.length }}</div>
          </div>

          <div v-else class="no-image">
            <div class="no-image-text-inline">Изображение не найдено</div>
          </div>
        </div>

        <!-- Пагинация для галереи (если найдено >1 изображений) -->
        <div v-if="images.length > 1" class="pager" aria-hidden="false">
          <button
            v-for="(img, i) in images"
            :key="img + '_' + i"
            class="pager-dot"
            :class="{ 'pager-dot--active': currentIndex === i }"
            @click="currentIndex = i"
            :aria-label="`Показать изображение ${i + 1}`"
          >
            {{ i + 1 }}
          </button>
        </div>

        <!-- Действия под картинкой -->
        <div class="actions">
          <button class="action-btn" @click="openRequestInfoModal" type="button">
            Запросить данные по образцу
          </button>

          <!-- Новая кнопка: скачать полный набор, показывается только для особых образцов -->
          <a
            v-if="isFullSample && downloadUrl"
            :href="downloadUrl"
            class="action-btn download-btn"
            target="_blank"
            rel="noopener"
          >
            Скачать полный набор данных
          </a>
        </div>
      </section>

      <!-- Правая часть — описание -->
      <section class="right-pane">
        <div class="panel">

          <!-- Подзаголовки и содержимое (оставлено без изменений) -->
          <div class="subsection">
            <h3 class="subsection-title">Описание образца</h3>
            <dl class="meta">
              <div class="row">
                <dt>Форма</dt>
                <dd>{{ displayValue({ key: 'form' }) }}</dd>
              </div>
              <div class="row">
                <dt>Размер, мм</dt>
                <dd>{{ formatNumberField('size_mm') }}</dd>
              </div>
              <div class="row">
                <dt>Масштаб</dt>
                <dd>{{ displayValue({ key: 'research_scale' }) }}</dd>
              </div>
              <div class="row">
                <dt>Краткое описание</dt>
                <dd>{{ displayValue({ key: 'short_description' }) }}</dd>
              </div>
            </dl>
          </div>

          <!-- ========== Важно: показываем/скрываем эти две секции в зависимости от категории ========== -->
          <!-- Секция "Геологическая информация" -->
          <div v-if="showGeoLabSections" class="subsection">
            <h3 class="subsection-title">Геологическая информация</h3>
            <dl class="meta">
              <div class="row"><dt>Категория</dt><dd>{{ displayValue({ key: 'gis_category' }) }}</dd></div>
              <div class="row"><dt>Бассейн</dt><dd>{{ displayValue({ key: 'basin' }) }}</dd></div>
              <div class="row"><dt>Структура I/II порядка</dt><dd>{{ combinedStructure }}</dd></div>
              <div class="row"><dt>Возраст</dt><dd>{{ displayValue({ key: 'department' }) }}</dd></div>
              <div class="row"><dt>Ярус</dt><dd>{{ displayValue({ key: 'tier' }) }}</dd></div>
              <div class="row"><dt>Свита / горизонт</dt><dd>{{ displayValue({ key: 'suite_horizon' }) }}</dd></div>
              <div class="row"><dt>Пласт</dt><dd>{{ displayValue({ key: 'layer' }) }}</dd></div>
              <div class="row"><dt>Генезис</dt><dd>{{ displayValue({ key: 'sample_origin_class' }) }}</dd></div>
              <div class="row"><dt>Текстуры по КТ</dt><dd>{{ displayValue({ key: 'textures_ct' }) }}</dd></div>
              <div class="row"><dt>Обстановка осадконакопления</dt><dd>{{ displayValue({ key: 'depositional_environment' }) }}</dd></div>
              <div class="row"><dt>Фация</dt><dd>{{ displayValue({ key: 'facies' }) }}</dd></div>
            </dl>
          </div>

          <!-- Секция "КТ-система" всегда показываем -->
          <div class="subsection">
            <h3 class="subsection-title">КТ-система</h3>
            <dl class="meta">
              <div class="row"><dt>Томограф</dt><dd>{{ displayValue({ key: 'scanner' }) }}</dd></div>
              <div class="row"><dt>Фильтр</dt><dd>{{ displayValue({ key: 'filter_used' }) }}</dd></div>
              <div class="row"><dt>Напряжение, кВ</dt><dd>{{ formatNumberField('voltage', 2) }}</dd></div>
              <div class="row"><dt>Ток, мА</dt><dd>{{ formatNumberField('current_ma', 2) }}</dd></div>
              <div class="row"><dt>Разрешение, мкм</dt><dd>{{ formatNumberField('resolution', 2) }}</dd></div>
            </dl>
          </div>

          <!-- Секция "Лабораторные данные" -->
          <div v-if="showGeoLabSections" class="subsection">
            <h3 class="subsection-title">Лабораторные данные</h3>
            <dl class="meta">
              <div class="row"><dt>Пористость, %</dt><dd>{{ formatNumberField('porosity', 2) }}</dd></div>
              <div class="row"><dt>Проницаемость, мД</dt><dd>{{ formatNumberField('permeability', 2) }}</dd></div>
              <div class="row"><dt>Дополнительные лабораторные данные</dt><dd>{{ displayValue({ key: 'other_lab_data' }) }}</dd></div>
            </dl>
          </div>
          <!-- ========== конец условной отрисовки ========== -->

        </div>
      </section>
    </div>

    <!-- TELEPORTED MODAL остался без изменений (просмотр изображений) -->
    <teleport to="body">
      <transition name="fade">
        <div v-if="isModalOpen" class="iv-modal" @click.self="closeModal" role="dialog" aria-modal="true">
          <button class="iv-modal-close" @click="closeModal" aria-label="Close image">✕</button>

          <template v-if="!hasImage || modalImageError">
            <div class="modal-noimage">
              <div class="noimage-box">
                <div class="noimage-ico">🖼️</div>
                <div class="noimage-text">Изображение отсутствует</div>
                <div class="noimage-sub">Нет доступных превью для этого образца.</div>
              </div>
            </div>
          </template>

          <template v-else>
            <button v-if="images.length > 1" class="iv-nav left" @click.stop="prevImage" aria-label="Previous image">‹</button>

            <div class="iv-modal-inner" @click.stop>
              <img :src="currentModalImage" class="iv-modal-img" alt="Full size" @error="onModalImageError" />
              <div v-if="images.length > 1" class="iv-modal-indicator">{{ modalIndex + 1 }} / {{ images.length }}</div>
            </div>

            <button v-if="images.length > 1" class="iv-nav right" @click.stop="nextImage" aria-label="Next image">›</button>
          </template>
        </div>
      </transition>
    </teleport>

    <!-- TELEPORTED MODAL: информационное окно для запроса данных (демоверсия) -->
    <teleport to="body">
      <transition name="fade">
        <div v-if="showRequestInfoModal" class="iv-modal" @click.self="closeRequestInfoModal" role="dialog" aria-modal="true">
          <div class="iv-modal-inner" style="max-width:720px; padding:0;">
            <div class="request-info-card" role="document" @click.stop>
              <header class="request-info-header">
                <h3>Запрос данных</h3>
              </header>

              <div class="request-info-body">
                <p>
                  Для получения данных по выбранному образцу, пожалуйста, напишите нам на почту
                  <span class="request-email">tomowebsolutions@yandex.ru</span>.
                </p>

                <p class="request-info-list-title">Данные, доступные для запроса:</p>

                <ul class="request-info-list">
                  <li>Исходные данные съёмки</li>
                  <li>Реконструированный стек</li>
                  <li>Лог-файл</li>
                  <li>Проект обработки данных</li>
                  <li>Дополнительные лабораторные данные</li>
                  <li>Физический образец</li>
                  <li>
                    Предобработанные данные:
                    <ul class="sublist">
                      <li>3D модель</li>
                      <li>Сегментированные (бинарные) стеки</li>
                      <li>Расчетные параметры по компонентам образца (объём, площадь поверхности, % содержания и т.д.)</li>
                    </ul>
                  </li>
                </ul>
              </div>

              <footer class="request-info-footer">
                <button class="action-btn" @click="closeRequestInfoModal">ОК</button>
              </footer>
            </div>
          </div>
        </div>
      </transition>
    </teleport>

  </main>

</template>

<script>
export default {
  name: 'ItemView',
  data() {
    return {
      item: {},
      images: [],
      currentIndex: 0,
      noDataText: '—',
      isModalOpen: false,
      modalIndex: 0,
      modalImageError: false,

      // ext order for FULL samples
      _fullSampleExts: ['bmp','png','jpg','webp'],
      _previewExts: ['webp','bmp','png','jpg','jpeg','tif','tiff'],
      // cache
      _foundCache: {},

      // info modal for "Запросить данные" (демоверсия)
      showRequestInfoModal: false
    }
  },

  computed: {
    title() {
      return (
        (this.item.renamed_id && String(this.item.renamed_id).trim()) ||
        (this.item.internal_id && String(this.item.internal_id).trim()) ||
        (this.item.fadr && String(this.item.fadr).trim()) ||
        this.noDataText
      )
    },

    hasImage() {
      return Array.isArray(this.images) && this.images.length > 0 && !this.modalImageError
    },

    currentImageSrc() {
      return this.images[this.currentIndex] || ''
    },

    currentModalImage() {
      return this.images[this.modalIndex] || this.images[0] || ''
    },

    combinedStructure() {
      const s1 = this.item.structure_i || (this.item.__raw && this.item.__raw.structure_i) || ''
      const s2 = this.item.structure_ii || (this.item.__raw && this.item.__raw.structure_ii) || ''
      if (s1 && s2) return `${s1}, ${s2}`
      return s1 || s2 || this.noDataText
    },

    // --- new: определяем, является ли образец "full sample" ---
    currentIdCandidates() {
      // Возвращаем набор возможных id (internal/renamed/fadr/id)
      const raw = this.item || this.item.__raw || {}
      return [
        String(this.item.internal_id || raw.internal_id || '').trim(),
        String(this.item.renamed_id || raw.renamed_id || '').trim(),
        String(this.item.fadr || raw.fadr || '').trim(),
        String(this.item.id || raw.id || '').trim()
      ].filter(Boolean)
    },

    isFullSample() {
      const keys = Object.keys(FULL_SAMPLE_DOWNLOADS).map(String)
      if (!keys.length) return false
      for (const cand of this.currentIdCandidates) {
        if (keys.includes(String(cand))) return true
      }
      return false
    },

    downloadUrl() {
      for (const cand of this.currentIdCandidates) {
        if (FULL_SAMPLE_DOWNLOADS[String(cand)]) return FULL_SAMPLE_DOWNLOADS[String(cand)]
      }
      return null
    },

    /**
     * NEW:
     * Показывать секции "Геологическая информация" и "Лабораторные данные"
     * только если категория образца явно относится к геологии/осадкам/палеонтологии.
     *
     * Логика:
     * - Берём значение поля gis_category (из item или item.__raw).
     * - Нормализуем (trim -> toLowerCase).
     * - Считаем, что категория подходит, если в строке содержится одно из ключевых слов:
     *   'геолог', 'осад', 'палеонтолог'
     *
     * Это покрывает варианты: "Геология", "Осадки", "Донные осадки", "Палеонтология" и т.п.
     * Если поле пустое или не содержит ключевых слов — секции скрываются.
     */
    showGeoLabSections() {
      try {
        const raw = this.item || this.item.__raw || {}
        const cat = (this.item.gis_category || raw.gis_category || '').toString().trim().toLowerCase()
        if (!cat) return false
        const keywords = ['геолог', 'осад', 'палеонтолог']
        return keywords.some(k => cat.includes(k))
      } catch (e) {
        return false
      }
    }
  },

  methods: {
    async fetchItem() {
      const url = `/api/v1/item/${this.$route.params.id}`
      const resp = await fetch(url)
      if (!resp.ok) throw new Error(`Не удалось загрузить образец (${resp.status})`)
      this.item = await resp.json()
    },

    /**
     * Simplified image finder:
     * - Prefer explicit preview_images / image_url from data.
     * - If sample is in FULL_SAMPLE_DOWNLOADS -> search for up to 4 images:
     *      {idKey}_1.{ext}, {idKey}_2.{ext}, {idKey}_3.{ext}, {idKey}_4.{ext}
     *   where ext in ['bmp','png','jpg'] (first ext that exists for each suffix is used).
     * - Otherwise look only for {idKey}_1.bmp
     */
    async findImages() {
      this.images = []
      this.modalImageError = false

      // 1) explicit preview_images / image_url
      const extract = (arr) => (Array.isArray(arr) ? arr.map(p => (typeof p === 'string' ? p : (p && p.url) ? p.url : null)).filter(Boolean) : [])
      const pi = extract(this.item.preview_images)
      if (pi.length) { this.images = pi; this.currentIndex = 0; return }

      const rawPi = extract(this.item.__raw && this.item.__raw.preview_images)
      if (rawPi.length) { this.images = rawPi; this.currentIndex = 0; return }

      if (this.item.__raw && this.item.__raw.image_url) { this.images = [this.item.__raw.image_url]; this.currentIndex = 0; return }
      if (this.item.image_url) { this.images = [this.item.image_url]; this.currentIndex = 0; return }

      // 2) build base path and idKey
      const raw = this.item.__raw || this.item
      const fadr = (this.item.fadr || raw.fadr || '').toString().trim()
      const idKey = (this.item.renamed_id || this.item.internal_id || raw.internal_id || this.item.id || raw.id || '').toString().trim()
      if (!fadr || !idKey) {
        this.images = []
        return
      }

      const cacheKey = `${fadr}||${idKey}`
      if (this._foundCache[cacheKey]) {
        this.images = Array.isArray(this._foundCache[cacheKey]) ? [...this._foundCache[cacheKey]] : []
        this.currentIndex = 0
        return
      }

      const found = []
      const baseEncoded = `/previews/${encodeURIComponent(fadr)}`
      const baseRaw = `/previews/${fadr}`

      if (this.isFullSample) {
        // --- оставляем старую логику для FULL samples без изменений ---
        for (let n = 1; n <= 4; n++) {
          const suffix = `_${n}`
          let foundForSuffix = null
          for (const ext of this._fullSampleExts) {
            const candidate = `${baseEncoded}/${encodeURIComponent(idKey)}${suffix}.${ext}`
            // sequential check (await) — acceptable for 4 images
            // eslint-disable-next-line no-await-in-loop
            const ok = await this._tryLoadImage(candidate)
            if (ok) {
              foundForSuffix = candidate
              break
            }
          }
          if (foundForSuffix) found.push(foundForSuffix)
          // safety cap
          if (found.length >= 4) break
        }
        // also accept single file without suffix as fallback for full-sample (optional)
        if (!found.length) {
          for (const ext of this._fullSampleExts) {
            const candidate = `${baseEncoded}/${encodeURIComponent(idKey)}.${ext}`
            // eslint-disable-next-line no-await-in-loop
            if (await this._tryLoadImage(candidate)) {
              found.push(candidate)
              break
            }
          }
        }
      } else {
        // --- Новая логика для non-full: пробуем набор расширений (как в ItemDetailPanel) ---
        // Сначала пробуем с закодированным fadr и закодированным именем
        let foundOne = false
        for (const base of [baseEncoded, baseRaw]) {
          for (const ext of this._previewExts) {
            const candidate = `${base}/${encodeURIComponent(idKey)}_1.${ext}`
            // eslint-disable-next-line no-await-in-loop
            if (await this._tryLoadImage(candidate)) {
              found.push(candidate)
              foundOne = true
              break
            }
          }
          if (foundOne) break

          // попробовать без encodeURIComponent для имени (raw filename)
          for (const ext of this._previewExts) {
            const candidate = `${base}/${idKey}_1.${ext}`
            // eslint-disable-next-line no-await-in-loop
            if (await this._tryLoadImage(candidate)) {
              found.push(candidate)
              foundOne = true
              break
            }
          }
          if (foundOne) break
        }
      }

      this._foundCache[cacheKey] = found.length ? [...found] : []
      this.images = [...this._foundCache[cacheKey]]
      this.currentIndex = 0
    },

    // load via Image() with small cache-bust; returns promise<boolean>
    _tryLoadImage(url) {
      return new Promise((resolve) => {
        try {
          const img = new Image()
          img.onload = () => resolve(true)
          img.onerror = () => resolve(false)
          img.src = url + (url.includes('?') ? '&_=' : '?_=') + Date.now()
        } catch (e) {
          resolve(false)
        }
      })
    },

    isEmpty(v) {
      return v === null || v === undefined || (typeof v === 'string' && v.trim() === '')
    },

    displayValue(field) {
      const primary = (this.item && (this.item[field.key] ?? (this.item.__raw && this.item.__raw[field.key]))) ?? undefined
      const fallback = field.fallbackKey ? (this.item && (this.item[field.fallbackKey] ?? (this.item.__raw && this.item.__raw[field.fallbackKey]))) : undefined
      const value = this.isEmpty(primary) ? fallback : primary
      return this.isEmpty(value) ? this.noDataText : value
    },

    formatNumberField(key, digits = 2) {
      const raw = (this.item && (this.item[key] ?? (this.item.__raw && this.item.__raw[key]))) ?? null
      if (raw === null || raw === undefined || raw === '') return this.noDataText
      const n = Number(String(raw).replace(',', '.'))
      if (!Number.isFinite(n)) return this.noDataText
      return Number.isFinite(n) ? n.toFixed(digits) : this.noDataText
    },

    // Новая логика: при клике открываем простой информационный модал
    openRequestInfoModal() {
      this.showRequestInfoModal = true
    },
    closeRequestInfoModal() {
      this.showRequestInfoModal = false
    },

    // Старая заглушка — теперь не используется, но оставлена для обратной совместимости
    onRequestData() {
      console.info('Запросить данные по образцу → демоверсия (используйте окно с инструкцией)')
    },

    onImageError(idx) {
      return () => {
        try {
          if (this.images && this.images.length > idx) {
            this.images.splice(idx, 1)
            if (this.currentIndex >= this.images.length) this.currentIndex = Math.max(0, this.images.length - 1)
          }
        } catch (e) { }
      }
    },

    openModal(startIndex = 0) {
      if (!this.images || this.images.length === 0) return
      this.modalIndex = Math.min(Math.max(0, startIndex), this.images.length - 1)
      this.isModalOpen = true
      this.modalImageError = false
      document.body.style.overflow = 'hidden'
      window.addEventListener('keydown', this.onKeydown)
    },

    closeModal() {
      this.isModalOpen = false
      this.modalImageError = false
      document.body.style.overflow = ''
      window.removeEventListener('keydown', this.onKeydown)
    },

    nextImage() {
      if (!this.images || this.images.length <= 1) return
      this.modalIndex = (this.modalIndex + 1) % this.images.length
    },
    prevImage() {
      if (!this.images || this.images.length <= 1) return
      this.modalIndex = (this.modalIndex - 1 + this.images.length) % this.images.length
    },

    onKeydown(e) {
      if (!this.isModalOpen) return
      if (e.key === 'Escape') this.closeModal()
      else if (e.key === 'ArrowRight') this.nextImage()
      else if (e.key === 'ArrowLeft') this.prevImage()
    },

    onModalImageError() {
      this.modalImageError = true
    }
  },

  async mounted() {
    await this.fetchItem()
    await this.findImages()
  },

  watch: {
    '$route.params.id': {
      immediate: false,
      async handler() {
        await this.fetchItem()
        await this.findImages()
      }
    }
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown)
    document.body.style.overflow = ''
  }
}

/* --- constants outside компонента --- */
const FULL_SAMPLE_DOWNLOADS = {
  'ABB_001': 'https://disk.360.yandex.ru/d/nbQkDy_KUeRQQA',
  'ADU_001': 'https://disk.360.yandex.ru/d/oRiNcq3Bx95giw',
  'AFT_019': 'https://disk.360.yandex.ru/d/xIyi7HSaUDQ0BQ',
  'AOH_001': 'https://disk.360.yandex.ru/d/zzjA5rpug34PcQ',
  'AOV_003': 'https://disk.360.yandex.ru/d/KIZ1Y7i6FAgIeA',
  'AYP_154': 'https://disk.360.yandex.ru/d/rFshG4f2OTZqqQ',
  'BBQ_001': 'https://disk.360.yandex.ru/d/Y2MjzLiyMTVwJA',
  'BEE_001': 'https://disk.360.yandex.ru/d/JvorTfJD4RrmEw'
}
</script>

<style scoped>
/* Общая раскладка под макет */
.item-page {
  background: #f3f4f6; /* светло-серый фон поля */
  min-height: calc(100vh - 120px); /* минус шапка+футер */
  padding: 16px 0 24px;
}

.item-shell {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(320px, 1fr) minmax(420px, 1fr);
  gap: 16px;
}

/* Левая колонка */
.left-pane {
  background: white;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
}

.item-title {
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 4px 0;
  color: #222222;
}

.image-wrap {
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 12px;
  overflow: hidden;
  background: #0f0f0f;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.image-wrap--empty {
  background: #f1f5f9;
  border: 1px dashed #cbd5e1;
}

.image-frame {
  width: 100%;
  height: 100%;
  display:flex;
  align-items:center;
  justify-content:center;
  position:relative;
  cursor:zoom-in;
}

.image {
  width:100%;
  height:100%;
  object-fit:contain;
  display:block;
  background:#000;
}

.img-counter {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0,0,0,0.6);
  color: #fff;
  padding: 6px 10px;
  border-radius: 12px;
  font-weight:700;
  font-size:13px;
}

/* Пагинация (под картинкой) */
.pager {
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
}

.pager-dot {
  border: none;
  background: #e5e7eb;
  border-radius: 10px;
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
}
.pager-dot--active {
  background: #111827;
  color: #fff;
}

/* Действия */
.actions {
  display:flex;
  gap:8px;
  flex-wrap:wrap;
}

.action-btn {
  border-radius: 10px;
  background: #222222;
  color: #ffffff;
  border: none;
  padding: 8px 12px;
  font-weight: 700;
  cursor: pointer;
}
.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(0,0,0,0.12);
}

/* Специально для кнопки "Скачать полный набор данных" — фон #222222 */
.download-btn {
  background: #222222 !important;
  color: #ffffff !important;
}

/* Правая колонка */
.right-pane {
  background: white;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
}

.panel-title {
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 12px 0;
  color: #222222;
}

.subsection { margin-top: 12px; }

.subsection-title {
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 8px 0;
  color: #222222;
}

.meta { display: grid; gap: 6px; }

.row {
  display: grid;
  grid-template-columns: 220px 1fr;
  align-items: start;
  gap: 12px;
  padding: 6px 0;
}

dt { color: #222222; font-size: 14px; line-height: 1.3; }
dd { margin: 0; font-size: 14px; color: #111827; line-height: 1.3; word-break: break-word; }

.text { font-size: 14px; color: #111827; }

/* ========== MODAL styles (teleported to body) ========== */
/* overlay + modal container */
.fade-enter-active, .fade-leave-active { transition: opacity .18s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.iv-modal {
  position: fixed;
  inset: 0;
  display:flex;
  align-items:center;
  justify-content:center;
  z-index:2147483000;
  background: rgba(0,0,0,0.72);
  padding: 24px;
  box-sizing: border-box;
  overflow: auto;
}

/* inner */
.iv-modal-inner {
  position: relative;
  max-width: calc(100% - 160px);
  max-height: calc(100vh - 80px);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

/* big image */
.iv-modal-img {
  max-width:100%;
  max-height:calc(100vh - 160px);
  width: auto;
  height: auto;
  border-radius: 12px;
  object-fit: contain;
  box-shadow: 0 8px 40px rgba(0,0,0,0.6);
}

/* nav */
.iv-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255,255,255,0.95);
  border: none;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  font-size: 28px;
  font-weight: 700;
  display:flex;
  align-items:center;
  justify-content:center;
  cursor:pointer;
  box-shadow:0 6px 18px rgba(0,0,0,0.25);
  z-index: 2147483010;
}
.iv-nav.left { left: 32px; }
.iv-nav.right { right: 32px; }

/* close button — фиксированная, вне затемнения */
.iv-modal-close {
  position: fixed;
  top: 18px;
  right: 18px;
  z-index: 2147483020;
  width: 44px;
  height: 44px;
  display:flex;
  align-items:center;
  justify-content:center;
  background:#fff;
  color:#222;
  border-radius:10px;
  border:none;
  box-shadow: 0 10px 30px rgba(0,0,0,0.45);
  cursor:pointer;
}

/* indicator */
.iv-modal-indicator {
  position: absolute;
  bottom: 18px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0,0,0,0.55);
  color:#fff;
  padding:6px 10px;
  border-radius:12px;
  font-weight:700;
}

/* noimage in modal */
.modal-noimage {
  width:100%;
  height:100%;
  display:flex;
  align-items:center;
  justify-content:center;
}
.noimage-box {
  text-align:center;
  color:#fff;
  padding:32px;
  border-radius:12px;
  background: rgba(0,0,0,0.25);
}
.noimage-ico { font-size:48px; margin-bottom:12px } .noimage-text { font-size:20px; font-weight:700; margin-bottom:6px } .noimage-sub { opacity:0.85; font-size:14px }

.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #64748b;
  text-align: center;
  padding: 12px;
}

.placeholder-large {
  width: 100%;
  height: calc(100% - 48px);
  display:flex;
  align-items:center;
  justify-content:center;
  border-radius: 12px;
  background: #f2f2f2;
  overflow: hidden;
}

.placeholder-img {
  max-width: 320px;
  width: auto;
  height: auto;
  opacity: 0.95;
}

.no-image-text-inline {
  font-weight: 700;
  color: #64748b;
}

/* ========== NEW: request info modal card styles ========= */
.request-info-card {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-height: calc(100vh - 120px);
  box-shadow: 0 10px 40px rgba(0,0,0,0.35);
  overflow: hidden;
  display:flex;
  flex-direction:column;
}

.request-info-header {
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0,0,0,0.06);
  background: linear-gradient(90deg, #f7fafc, #ffffff);
}
.request-info-header h3 { margin:0; font-size:18px; color:#111; }

.request-info-body {
  padding: 18px 20px;
  color: #222;
  line-height:1.5;
  font-size:16px;
  overflow: auto;
  min-height: 120px;
}

.request-info-body p { margin: 0 0 12px 0; }
.request-info-list-title { font-weight:700; margin-bottom:8px; }

.request-info-list { margin:0 0 0 18px; padding:0 0 0 0; }
.request-info-list li { margin-bottom:8px; }

.request-info-list .sublist { margin-top:6px; margin-left:14px; }
.request-email {
  display:inline-block;
  font-weight:700;
  color: #32595F;
  margin-left:6px;
}

/* footer */
.request-info-footer {
  flex: 0 0 auto;
  padding: 12px 16px;
  border-top: 1px solid rgba(0,0,0,0.06);
  display:flex;
  justify-content:flex-end;
  gap:8px;
  background: #fafafa;
}

/* адаптив */
@media (max-width: 980px) {
  .item-shell { grid-template-columns: 1fr; }
  .row { grid-template-columns: 1fr; }
  .img-counter { top: 8px; right: 8px; padding: 4px 8px; font-size: 12px; }
}

@media (max-height: 700px) {
  .iv-modal { padding: 10px; }
  .iv-modal-inner { max-height: calc(100vh - 40px); }
  .request-info-card { max-height: calc(100vh - 48px); }
  .request-info-body { padding: 12px; }
}

</style>
